import * as React from "react";

/**
 * Hook to create safe useReducer dispatch function
 *
 * This only runs a dispatch if the component is still mounted, otherwise it
 * returns undefined to prevent memory leaks from async functions, for example.
 */
const useSafeDispatch = <T>(dispatch: React.Dispatch<T>) => {
  // The component is not rendered yet when hook is first called in the component logic
  const isMounted = React.useRef(false);

  React.useLayoutEffect(() => {
    // Now that a layout effect has run, the component has been mounted into the DOM
    isMounted.current = true;
    // This cleanup function will run when the component is unmounted
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Return a safe dispatch function
  return React.useCallback(
    (action) => (isMounted.current ? dispatch(action) : undefined),
    [dispatch]
  );
};

type UseAsyncState<TData, TError> = {
  status: "idle" | "loading" | "error" | "success";
  data: TData | null;
  error: TError | null;
};

/**
 * Hook to track async function state and result
 *
 * Similar to react-query's useQuery hook, but returns a `run` function that accepts
 * any Promise which resolves into `data` or rejects into `error`. For example:
 * ```
 * const { data, error, status, run } = useAsync();
 * const onClick = () => run(fetchUser("foo"));
 * ```
 */
const useAsync = <TData, TError = Error>(
  initialState: Partial<UseAsyncState<TData, TError>> = {}
) => {
  const initialStateRef = React.useRef<UseAsyncState<TData, TError>>({
    status: "idle",
    data: null,
    error: null,
    ...initialState,
  });

  const [{ status, data, error }, dispatch] = React.useReducer(
    // Simple reducer to merge new values from action into existing state
    (
      state: UseAsyncState<TData, TError>,
      action: Partial<UseAsyncState<TData, TError>>
    ) => ({ ...state, ...action }),
    initialStateRef.current
  );

  const safeDispatch = useSafeDispatch(dispatch);
  const setData = React.useCallback(
    (_data) => safeDispatch({ data: _data, status: "success", error: null }),
    [safeDispatch]
  );
  const setError = React.useCallback(
    (_error) => safeDispatch({ error: _error, status: "error" }),
    [safeDispatch]
  );

  // Wrap the async function call and handle setting status and result/error.
  const run = React.useCallback(
    (promise: Promise<TData>) => {
      safeDispatch({ status: "loading" });

      return promise
        .then((result: TData) => {
          setData(result);
          return result;
        })
        .catch((err) => {
          setError(err);
          return err;
        });
    },
    [safeDispatch, setData, setError]
  );

  return {
    run,
    data,
    error,
    status,
    isIdle: status === "idle",
    isLoading: status === "loading",
    isSuccess: status === "success",
    isError: status === "error",
  };
};

/**
 * Hook to observe if an element has scrolled into/out of view
 *
 * Returns ref to attach to element to be observed and the resulting boolean.
 * const { scrollRef, isVisible } = useScrollObserver();
 * <div ref={scrollRef}>{isVisible ? "hello" : "goodbye"}</div>
 */
const useScrollObserver = (initialValue = false) => {
  const [element, setElement] = React.useState<Element | null>(null);
  const [isVisible, setIsVisible] = React.useState(initialValue);

  // Create callback ref because useRef is mutable and doesn’t notify us about
  // changes to its current value.
  const scrollRef = React.useCallback((node) => {
    if (node !== null) {
      setElement(node);
    }
  }, []);

  // Layout effects wait until DOM mutations, ensuring we have the element reference
  React.useLayoutEffect(() => {
    // If there is no element yet, do nothing
    if (!element) return () => {};

    const scrollObserver = new IntersectionObserver((entries) => {
      // Get element position and dimensions
      const { y, height } = entries[0].boundingClientRect;
      // Verify the bottom of the element is below the top of the viewport
      const belowViewportTop = y + height > 0;
      // Verify the top of the element is above the bottom of the viewport
      const aboveViewportBottom = y < window.innerHeight;
      // If both are true, the element is visible
      setIsVisible(belowViewportTop && aboveViewportBottom);
    });

    // Attach the observer to the DOM node
    scrollObserver.observe(element);

    // Stop observer when the component is unmounted
    return () => {
      scrollObserver.disconnect();
    };
  }, [element]);

  return { scrollRef, isVisible };
};

/**
 * Hook that debounces a value that changes too frequently
 * @param value - Value that should be updated after timeout
 * @param delay - Delay for effect in milliseconds
 */
const useDebouncer = <T>(value: T, delay: number) => {
  const [debounced, setDebounced] = React.useState<T>(value);
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setDebounced(value);
    }, delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);
  return debounced;
};

export { useAsync, useSafeDispatch, useScrollObserver, useDebouncer };
